"use client"

import { useState } from "react"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { FIELD_UNDERLINE, FieldShell } from "@/components/graphite/field"
import { PageAction } from "@/components/graphite/page-header"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { useNavigate } from "@tanstack/react-router"
import { usePostBuckets, usePutBucketsBucketId } from "openapi/api/endpoints"
import { useCommunity, type BucketRow } from "@/hooks/use-community"
import { BucketsTable, CommunityToolbar } from "./graphite-community-tables"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { AxiosError } from "axios"

import { asArray } from "@/lib/as-array"
import type { Bucket } from "openapi/api/model"

interface BucketManagerProps {
  //selectedBucket: string | null
  //onSelectBucket: (bucketId: string | null) => void
  searchTerm: string
}


export function BucketManager({ searchTerm }: BucketManagerProps) {
  /*
    The tab used to be handed a search term it was never given one of. The
    search lives on the toolbar now, where a reader can see it.
  */
  const [search, setSearch] = useState(searchTerm)
  const community = useCommunity()
  const navigate = useNavigate()
  const queryClient=useQueryClient()
  const {mutateAsync:createBucket}=usePostBuckets({
    mutation:{
      onMutate({ data }) {
        /*
          Spreading the cache crashed when nothing was cached yet — the first
          bucket anyone creates in a session. And the optimistic row carries no
          id, which the table filters out, so the write is not shown early; it
          only has to leave the cache intact until the refetch.
        */
        const currentBuckets = queryClient.getQueryData(["/buckets"])
        queryClient.setQueryData(
          ["/buckets"],
          [...asArray<Bucket>(currentBuckets), data],
        )
        return { currentBuckets }
      },
      onError(__, _, onMutateResult) {
        queryClient.setQueryData(["/buckets"],onMutateResult?.currentBuckets)
      },

    }
  })
  const { mutateAsync: updateBucket } = usePutBucketsBucketId()

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [newBucket, setNewBucket] = useState({
    name: "",
    description: "",
    touchPointInterval: 7,
  })

  const handleCreateBucket = async () => {
    try{
      await createBucket({data:{
        name:newBucket.name,
        description:newBucket.description,
        interval_days:newBucket.touchPointInterval
      }})
      await queryClient.invalidateQueries({ queryKey: [`/buckets`], exact: false })
      await queryClient.refetchQueries({ queryKey: [`/buckets`], exact: false })
      setIsCreateDialogOpen(false)
      toast.success("Created!",{
        description: 'Bucket created successfully',
        action: {
          label: "Dismiss",
          onClick: () => {},
        },
        style: {
          background: '#a3e635'
        }
      })
    }catch(err){
      console.log('Error: ',err);
      let description = "Something went wrong."
      const axiosError = err as AxiosError<any>
      const rawError = axiosError.response?.data?.error

      if (typeof rawError === "string") {
        try {
          const parsed = JSON.parse(rawError)
          if (Array.isArray(parsed) && parsed[0]?.message) {
            description = parsed[0].message
          } else if (parsed?.message) {
            description = parsed.message
          } else {
            description = rawError
          }
        } catch {
          description = rawError
        }
      }
      
      toast.error("An error occured!",{
        description,
        action: {
          label: "Dismiss",
          onClick: () => {},
        },
        style: {
          background: "#dc2626",
        },
      });
    }
  }

  /*
    This was an empty function behind a live-looking control. `PUT /buckets/{id}`
    takes a status, so Pause and Start now do what they say and the list
    refetches.
  */
  const toggleBucketStatus = async (row: BucketRow) => {
    try {
      await updateBucket({
        bucketId: row.id,
        data: { status: row.status === "active" ? "paused" : "active" },
      })
      await queryClient.invalidateQueries({ queryKey: ["/buckets"], exact: false })
    } catch (error) {
      toast.error("Could not change the bucket", { description: `${error}` })
    }
  }

  const handleBucketClick = (bucketId: string) => {
    navigate({to:`/buckets/${bucketId}`})
  }

  const needle = search.trim().toLowerCase()
  const rows = community.bucketRows.filter(
    (row) =>
      !needle ||
      row.name.toLowerCase().includes(needle) ||
      (row.description ?? "").toLowerCase().includes(needle),
  )

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/*
        17e: "A bucket is a promise to stay in touch every N days, so the row
        shows whether that promise is kept." The card showed the interval and
        never whether anyone was overdue — the only reason to open the page.
      */}
      <CommunityToolbar
        placeholder="Search buckets"
        value={search}
        onChange={setSearch}
        action="Create Bucket"
        onAction={() => setIsCreateDialogOpen(true)}
      />

      <BucketsTable
        rows={rows}
        isLoading={community.isLoadingBuckets}
        onOpen={handleBucketClick}
        onToggleStatus={toggleBucketStatus}
      />

      <DialogShell
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        title="Create New Bucket"
        description="A bucket is a set of people you want to stay in front of, on a rhythm."
        footer={
          <PageAction tone="primary" onClick={handleCreateBucket}>
            Create Bucket
          </PageAction>
        }
      >
        <FieldShell label="Bucket name">
          <input
            id="name"
            value={newBucket.name}
            onChange={(e) => setNewBucket({ ...newBucket, name: e.target.value })}
            placeholder="e.g., Senior Engineers"
            aria-label="Bucket name"
            className={cn(FIELD_UNDERLINE, "pb-[7px] text-[12.5px] leading-[1.4]")}
          />
        </FieldShell>

        <FieldShell label="Description" className="mt-5">
          <textarea
            id="description"
            value={newBucket.description}
            onChange={(e) => setNewBucket({ ...newBucket, description: e.target.value })}
            placeholder="Describe the target audience for this bucket..."
            rows={3}
            aria-label="Description"
            className={cn(FIELD_UNDERLINE, "resize-none pb-2.5 text-[12.5px] leading-[1.7]")}
          />
        </FieldShell>

        <FieldShell label="Touch point interval" className="mt-5">
          <Select
            value={newBucket.touchPointInterval.toString()}
            onValueChange={(value) =>
              setNewBucket({ ...newBucket, touchPointInterval: Number.parseInt(value) })
            }
          >
            <SelectTrigger
              aria-label="Touch point interval"
              className={cn(FIELD_UNDERLINE, "h-auto pb-[7px] text-[12.5px] leading-[1.4]")}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Every 3 days</SelectItem>
              <SelectItem value="7">Weekly</SelectItem>
              <SelectItem value="14">Bi-weekly</SelectItem>
              <SelectItem value="21">Every 3 weeks</SelectItem>
              <SelectItem value="30">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </FieldShell>
      </DialogShell>
    </div>
  )
}
